import type { TeamOut } from '@repo/api-codegen';
import {
	apiListTeamsV2QueryKey,
	fetchApiListTeamsV2,
} from '@repo/api-codegen/v2';

import { IconWrapper } from '@repo/common/components/IconWrapper';
import ItemIcon from '@repo/common/components/ItemIcon';
import TagIcon from '@repo/common/components/TagIcon/TagIcon';
import { Icon } from '@repo/foundations';

import { startCase } from 'lodash-es';
import type { ICollection, ITag } from '../../../../api';
import {
	COLLECTIONS_NAMESPACE,
	collectionsQueryKeyFactory,
	fetchCollectionList,
	fetchTagList,
	queryClient,
	TAGS_NAMESPACE,
	tagsQueryKeyFactory,
} from '../../../../api';
import {
	activeUsersAndGroupsGetItemsFn,
	fetchQuery,
} from '../../../../components/Filter/filterConfigUtils';
import { AutomationEditOption, type EditItemFunction } from '../../types';
import { buildGetItemsById, buildGetItemsByIdFromSearch } from '../../utils';

export const EDIT_ACTION_VALUE_ITEMS: Record<string, EditItemFunction> = {
	[AutomationEditOption.TAG]: {
		getItems: async (page: number = 1, searchTerm: string = '') => {
			const getQueryKey = (filterPage: number, filterSearchTerm: string) =>
				tagsQueryKeyFactory.list(filterPage, {
					name__icontains: filterSearchTerm,
					visible: true,
				});
			const getQueryFn = (filterPage: number, filterSearchTerm: string) =>
				fetchTagList({
					filters: { name__icontains: filterSearchTerm, visible: true },
				});
			const filterFn = (filterSearchTerm: string, result: ITag[]) =>
				result.filter(
					(tag) =>
						!searchTerm ||
						tag.name.toLowerCase().includes(filterSearchTerm.toLowerCase())
				);

			const results = await fetchQuery<ITag>(
				getQueryKey,
				getQueryFn,
				page,
				searchTerm,
				filterFn
			);

			return (
				results?.map((tag: ITag) => ({
					label: startCase(tag.name),
					value: tag.id,
					icon: <TagIcon color={tag.color} />,
				})) ?? []
			);
		},
		getItemsById: buildGetItemsById(TAGS_NAMESPACE),
	},
	[AutomationEditOption.COLLECTION]: {
		getItems: async (page: number = 1, searchTerm: string = '') => {
			const getQueryKey = (filterPage: number, filterSearchTerm: string) =>
				collectionsQueryKeyFactory.list(filterPage, {
					filters: { search_term: filterSearchTerm },
				});
			const getQueryFn = (filterPage: number, filterSearchTerm: string) =>
				fetchCollectionList({
					page: filterPage,
					filters: { search_term: filterSearchTerm },
				});
			const filterFn = (filterSearchTerm: string, result: ICollection[]) =>
				result.filter(
					(collection) =>
						!searchTerm ||
						collection.title
							?.toLowerCase()
							.includes(filterSearchTerm.toLowerCase())
				);

			const results = await fetchQuery<ICollection>(
				getQueryKey,
				getQueryFn,
				page,
				searchTerm,
				filterFn
			);

			return results?.map((collection: ICollection) => ({
				label: startCase(collection.title),
				value: collection.id,
				icon: (
					<IconWrapper>
						<ItemIcon
							type="emoji"
							item={{ icon: collection.icon }}
							color="icon/brand/default"
						/>
					</IconWrapper>
				),
			}));
		},
		getItemsById: buildGetItemsById(COLLECTIONS_NAMESPACE),
	},
	[AutomationEditOption.OWNER]: {
		getItems: activeUsersAndGroupsGetItemsFn,
		getItemsById: buildGetItemsByIdFromSearch,
	},
	[AutomationEditOption.SUBSCRIBER]: {
		getItems: activeUsersAndGroupsGetItemsFn,
		getItemsById: buildGetItemsByIdFromSearch,
	},
	[AutomationEditOption.TEAM]: {
		getItems: async (page?: number, searchTerm?: string) => {
			const teams = await queryClient.fetchQuery({
				queryKey: apiListTeamsV2QueryKey({}),
				queryFn: ({ signal }) => fetchApiListTeamsV2({}, signal),
			});

			return teams
				?.map((team: TeamOut) => ({
					label: team.name,
					value: team.id,
					icon: <IconWrapper>{team.icon}</IconWrapper>,
				}))
				?.filter(
					(item: { label: string; value: string; icon: JSX.Element }) =>
						!searchTerm ||
						item.label.toLowerCase().includes(searchTerm.toLowerCase())
				);
		},
	},
	[AutomationEditOption.PII]: {
		getItems: [
			{
				label: 'PII',
				value: true,
				icon: <Icon name="PII" fillColor="icon/caution/default" />,
			},
			{
				label: 'Non-PII',
				value: false,
				icon: <Icon name="notPII" />,
			},
		],
	},
	[AutomationEditOption.VERIFIED]: {
		getItems: [
			{
				label: 'Verified',
				value: true,
				icon: <Icon name="verified" fillColor="fill/emphasis/default" />,
			},
			{
				label: 'Not verified',
				value: false,
				icon: <Icon name="verifiedUnfilled" color="icon/secondary/default" />,
			},
		],
	},
	[AutomationEditOption.PUBLISHED]: {
		getItems: [
			{
				label: 'Published',
				value: true,
				icon: (
					<Icon name="circleCheckFilled" fillColor="icon/success/default" />
				),
			},
			{
				label: 'Draft',
				value: false,
				icon: <Icon name="circle" color="icon/secondary/default" />,
			},
		],
	},
};
